import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SERVICES = [
  "IT Asset Disposition (ITAD)",
  "Secure Data Destruction",
  "Data Center Decommissioning",
  "Technology Lifecycle Management",
  "IT Asset Buyback & Value Recovery",
  "Reverse Logistics & Asset Recovery",
  "Device Refurbishment & Redeployment",
  "Sustainable E-Waste Recycling",
  "Global Program Management",
];

const inputCls =
  "w-full bg-background border border-border rounded-none px-4 py-3.5 font-sans text-sm text-foreground placeholder:font-mono placeholder:text-muted-foreground/60 placeholder:text-xs placeholder:uppercase placeholder:tracking-wider focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors";

export default function QuoteSection() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", service: "", asset_count: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please fill name, email and service.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", form);
      setDone(data.reference);
      toast.success(`Quote request received — ${data.reference}`);
      setForm({ name: "", email: "", company: "", phone: "", service: "", asset_count: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="bg-[#0a0a0a] text-[#f7f7f6] py-20 md:py-32" data-testid="quote-section">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-4">/ Get started</p>
            <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl md:text-6xl leading-[0.88] mb-6">
              Request a<br />free quote.
            </h2>
            <p className="font-sans text-white/60 leading-relaxed max-w-sm mb-8">
              Tell us what you need to retire. We&apos;ll respond within one business day with a
              tailored, no-obligation plan and pricing.
            </p>
            <div className="font-mono text-xs text-white/40 space-y-2">
              <p>→ No minimum volume</p>
              <p>→ On-site collection available</p>
              <p>→ Full certificates included</p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 bg-[#050505] border border-white/10 p-6 md:p-10"
          >
            {done ? (
              <div data-testid="quote-success" className="py-16 text-center">
                <div className="font-mono text-xs uppercase tracking-widest text-primary mb-3">Request received</div>
                <div className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-4">{done}</div>
                <p className="text-white/60 font-sans text-sm mb-8">Keep this reference. Our team will be in touch within one business day.</p>
                <button onClick={() => setDone(null)} className="border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-wider hover:border-primary hover:text-primary transition-colors">
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quote-form">
                <input data-testid="quote-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="quote-email" type="email" className={inputCls} placeholder="Work email *" value={form.email} onChange={set("email")} />
                <input data-testid="quote-company" className={inputCls} placeholder="Company" value={form.company} onChange={set("company")} />
                <input data-testid="quote-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />

                <div className="md:col-span-1">
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="quote-service" className="w-full rounded-none border-border bg-background text-foreground h-[50px] font-sans text-sm data-[placeholder]:text-muted-foreground/60 data-[placeholder]:font-mono data-[placeholder]:text-xs data-[placeholder]:uppercase focus:ring-primary/20">
                      <SelectValue placeholder="Service required *" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} className="rounded-none font-sans text-sm">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <input data-testid="quote-count" className={inputCls} placeholder="Approx. asset count" value={form.asset_count} onChange={set("asset_count")} />

                <textarea data-testid="quote-message" rows={4} className={`${inputCls} md:col-span-2 resize-none`} placeholder="Tell us about your assets…" value={form.message} onChange={set("message")} />

                <button
                  type="submit"
                  data-testid="quote-submit"
                  disabled={loading}
                  className="md:col-span-2 group flex items-center justify-center gap-2 bg-primary text-white py-4 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors active:scale-[0.99] disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <>Submit request <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
